import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./BottomTabBar.css";
import { ROUTES } from "../../utils/appRoutes";

/**
 * The phone navigation: PLAY and PROFILE pinned to the bottom of the screen,
 * the same two tabs as the mobile app.
 *
 * Only shown at phone widths (see BottomTabBar.css), where it replaces the top
 * navbar. Signed-out visitors get REGISTER and LOGIN in place of the two tabs.
 *
 * Like the app, it stays off the sign-in screens: those are a single task to
 * finish, not somewhere to navigate from.
 */

const AUTH_PATHS = new Set([
  ROUTES.LOGIN,
  ROUTES.REGISTER,
  ROUTES.CONFIRM,
  ROUTES.FORGOT_PASSWORD,
  ROUTES.RESET_PASSWORD,
]);

// Ionicons 7 "football-outline" and "person-outline", the icons the app uses.
function FootballIcon() {
  return (
    <svg viewBox="0 0 512 512" aria-hidden="true" focusable="false">
      <circle cx="256" cy="256" r="192" fill="none" stroke="currentColor" strokeLinecap="round" strokeMiterlimit="10" strokeWidth="32" />
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="32"
        d="M256 175.15l-76.09 63.83L200 320h112l20.09-81.02L256 175.15zM332.09 238.98l52.87-22.4 25.78-73.26M447 269.97l-62.04-53.39M179.91 238.98l-52.87-22.4-25.78-73.26M65 269.97l62.04-53.39M256 175.15v-57.57l64-42.64M192 74.93l64 42.65M312 320l28 48-28 71M410.74 368H342M200 320l-28 48 28.37 71.5M101.63 368H172"
      />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg viewBox="0 0 512 512" aria-hidden="true" focusable="false">
      <path d="M344 144c-3.92 52.87-44 96-88 96s-84.15-43.12-88-96c-4-55 35-96 88-96s92 42 88 96z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" />
      <path d="M256 304c-87 0-175.3 48-191.64 138.6C62.39 453.52 68.57 464 80 464h352c11.44 0 17.62-10.48 15.65-21.4C431.3 352 343 304 256 304z" fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth="32" />
    </svg>
  );
}

// Ionicons 7 "person-add-outline" and "log-in-outline", for signed-out visitors.
function PersonAddIcon() {
  return (
    <svg viewBox="0 0 512 512" aria-hidden="true" focusable="false">
      <path d="M376 144c-3.92 52.87-44 96-88 96s-84.15-43.12-88-96c-4-55 35-96 88-96s92 42 88 96z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" />
      <path d="M288 304c-87 0-175.3 48-191.64 138.6-2 10.92 4.21 21.4 15.65 21.4H464c11.44 0 17.62-10.48 15.65-21.4C463.3 352 375 304 288 304z" fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth="32" />
      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" d="M88 176v112M144 232H32" />
    </svg>
  );
}

function LogInIcon() {
  return (
    <svg viewBox="0 0 512 512" aria-hidden="true" focusable="false">
      <path d="M192 176v-40a40 40 0 0140-40h160a40 40 0 0140 40v240a40 40 0 01-40 40H240c-22.09 0-48-17.91-48-40v-40" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" />
      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" d="M288 336l80-80-80-80M80 256h272" />
    </svg>
  );
}

// Visitors who are not signed in have nothing behind Play or Profile yet, so
// the bar offers the two ways in instead.
const SIGNED_OUT_TABS = [
  {
    key: "register",
    label: "Register",
    to: ROUTES.REGISTER,
    Icon: PersonAddIcon,
    isActive: (path) => path === ROUTES.REGISTER,
  },
  {
    key: "login",
    label: "Login",
    to: ROUTES.LOGIN,
    Icon: LogInIcon,
    isActive: (path) => path === ROUTES.LOGIN,
  },
];

const TABS = [
  {
    key: "play",
    label: "Play",
    to: ROUTES.HOME,
    Icon: FootballIcon,
    // A game is something you reach from Play, so Play stays lit there.
    isActive: (path) => path === ROUTES.HOME || path.startsWith(`${ROUTES.GAMES}/`),
  },
  {
    key: "profile",
    label: "Profile",
    to: ROUTES.PROFILE,
    Icon: PersonIcon,
    isActive: (path) => path === ROUTES.PROFILE,
  },
];

function BottomTabBar({ isAuthenticated = false }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const visible = !AUTH_PATHS.has(pathname);

  // The page only reserves room at the bottom while the bar is there.
  useEffect(() => {
    document.body.classList.toggle("has-bottom-tab-bar", visible);
    return () => document.body.classList.remove("has-bottom-tab-bar");
  }, [visible]);

  if (!visible) return null;

  const tabs = isAuthenticated ? TABS : SIGNED_OUT_TABS;

  return (
    <nav className="bottom-tab-bar" aria-label="Main">
      {tabs.map(({ key, label, to, Icon, isActive }) => {
        const active = isActive(pathname);
        return (
          <button
            key={key}
            type="button"
            className={`bottom-tab ${active ? "bottom-tab--active" : ""}`}
            aria-current={active ? "page" : undefined}
            onClick={() => navigate(to)}
          >
            <span className="bottom-tab-icon">
              <Icon />
            </span>
            <span className="bottom-tab-label">{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

export default BottomTabBar;
